import { nameof } from "bindable-data";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { IDestroyOptions, Sprite, Texture, Text, TextStyle, Rectangle } from "pixi.js";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { DefaultButton } from "../../../../common/components/DefaultButton";
import { IPlayerModel } from "../../../../common/models/IPlayerModel";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { MarketplaceCard } from "../components/MarketplaceCard";
import { MarketplaceMap } from "../components/MarketplaceMap";
import { MarketplaceScrollBox } from "../components/MarketplaceScrollBox";
import { IMarketplaceViewModel } from "../models/IMarketplaceViewModel";
import { IMarketplaceController } from "../models/controller/IMarketplaceController";
import { IMarketplaceMapModel } from "../models/map/IMarketplaceMapModel";
import { MarketplaceListItem } from "../models/map/MarketplaceListItem";
import { gsap } from "gsap";
import { FullBuildingContainer } from "../components/fullbuilding/FullBuildingContainer";
import { GameFont } from "../../../../common/GameFont";
import { IMarketplaceBuildingModel } from "../models/building/IMarketplaceBuildingModel";
import { MarketplaceNewsPresenter } from "../components/news/MarketplaceNewsPresenter";
import { IMarketplaceNewsModel } from "../models/news/IMarketplaceNewsModel";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { BaseScalableView } from "../../../../common/BaseScalableView";
import { DiscountContainer } from "../components/DiscountContainer";
import { IBenefitsModel } from "../../../../common/models/benefits/IBenefitsModel";
import { IFirstTimeExperienceModel } from "../../../../common/models/IFirstTimeExperienceModel";
import { UserProfileResourcePackage } from "../../../userprofile/resources/UserProfileResourcePackage";
import { ILogger } from "../../../../../logger/ILogger";
import { CancellationToken } from "async/CancellationToken";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { BuildingFilterSwitcher } from "../components/BuildingFilterSwitcher";
import { MarketplaceResourcePackage } from "../../resources/MarketplaceResourcePackage";
import { MapFilter } from "../models/map/MapFilter";
import { VBox } from "../../../../common/components/ui/VBox";

export class MarketplaceView extends BaseScalableView<void> {
    private static readonly HEADER_HEIGHT = 205;
    private static readonly HEADER_TEXT = "MARKETPLACE";

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly marketplaceResources: MarketplaceResourcePackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly marketplaceController: IMarketplaceController;
    private readonly marketplaceMapModel: IMarketplaceMapModel;
    private readonly marketplaceMapViewModel: IMarketplaceViewModel;
    private readonly marketplaceCards: Array<MarketplaceCard>;
    private readonly playerModel: IPlayerModel;
    private readonly marketplaceBuildingModel: IMarketplaceBuildingModel;
    private readonly marketplaceNewsModel: IMarketplaceNewsModel;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly benefitsModel: IBenefitsModel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly logger: ILogger;
    private readonly web3Model: Web3Model;

    private marketplaceMap: MarketplaceMap;
    private mapMask: Sprite;
    private backButton: DefaultButton;
    private headerText: Text;
    private zoomButton: ButtonWithScaleAnimation;
    private scrollBox: MarketplaceScrollBox;
    private closestCard: MarketplaceCard;
    private scrollBoxInLoadedState: boolean;
    private marketplaceFullBuilding: FullBuildingContainer;
    private newsPresenter: MarketplaceNewsPresenter;
    private buildingArea: number;
    private scrollBoxUpdateAnimation: gsap.core.Timeline;
    private cardClickAnimation: gsap.core.Timeline;

    private leftBox: VBox;
    private discountContainer: DiscountContainer;
    private buildingFilterSwitcher: BuildingFilterSwitcher;

    public constructor(
        pcs: PromiseCompletionSource<void>,
        cancellationToken: CancellationToken,
        commonResources: CommonResourcesPackage,
        marketplaceResources: MarketplaceResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        canvasScaler: ICanvasScaler,
        marketplaceController: IMarketplaceController,
        marketplaceMapModel: IMarketplaceMapModel,
        marketplaceMapViewModel: IMarketplaceViewModel,
        playerModel: IPlayerModel,
        marketplaceBuildingModel: IMarketplaceBuildingModel,
        marketplaceNewsModel: IMarketplaceNewsModel,
        buildingArea: number,
        analyticsProvider: IAnalyticProviderWrapper,
        benefitsModel: IBenefitsModel,
        firstTimeExperienceModel: IFirstTimeExperienceModel,
        logger: ILogger,
        web3Model: Web3Model
    ) {
        super(pcs, canvasScaler);
        this.cancellationToken = cancellationToken;
        this.commonResources = commonResources;
        this.marketplaceResources = marketplaceResources;
        this.userProfileResources = userProfileResources;
        this.marketplaceController = marketplaceController;
        this.marketplaceMapModel = marketplaceMapModel;
        this.marketplaceMapViewModel = marketplaceMapViewModel;
        this.marketplaceBuildingModel = marketplaceBuildingModel;
        this.playerModel = playerModel;
        this.marketplaceNewsModel = marketplaceNewsModel;
        this.buildingArea = buildingArea;
        this.analyticsProvider = analyticsProvider;
        this.benefitsModel = benefitsModel;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.logger = logger;
        this.web3Model = web3Model;
        this.marketplaceCards = [];
        this.closestCard = null;
        this.scrollBoxInLoadedState = false;
        this.scrollBoxUpdateAnimation = new gsap.core.Timeline();
        this.cardClickAnimation = new gsap.core.Timeline();
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.eventMode = "static";

        this.zoomButton.onUp.add(this.onZoomClick, this);
        this.buildingFilterSwitcher.switcher.onChange.connect(this.filterSwitchHandle);
        this.discountContainer.button.onUp.add(this.discountButtonClickHandle, this);
        this.backButton.onUp.add(this.onExitClick, this);

        this.marketplaceMapModel.propertyChanged.add(this.onMarketplaceMapModelPropertyChanged, this);
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
        this.marketplaceFullBuilding.onTransactionHandled.add(this.transactionCompleteHandle, this);
        this.once("added", this.addedToStage.bind(this));
    }

    public async start(): Promise<void> {
        const discountAvailable = this.firstTimeExperienceModel.piggyIconInLobby.value && this.playerModel.tokens.value != undefined;
        this.discountContainer.visible = this.benefitsModel.benefits.value?.length > 0 ? true : discountAvailable;
    }

    private async onMarketplaceMapModelPropertyChanged(model: IMarketplaceMapModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "buildings"):
                if (this.marketplaceMapViewModel.currentMapFilter.value.offset === 0) {
                    this.replaceScrollBoxItems();
                } else {
                    this.addNewItemsToScrollBox();
                }
                this.scrollBoxInLoadedState = false;
                break;
        }
    }

    private transactionCompleteHandle(): void {
        const targetCard = this.marketplaceCards?.find(card => this.marketplaceMapViewModel.selectedMarketplaceCard.value.listItem.id === card.listItem.id);
        if (targetCard != null) {
            this.scrollToMarketplaceItem(targetCard);
        }
    }

    private onCancellationRequested(): void {
        this.operationControls.complete();
        this.destroy();
    }

    private build(): void {
        const viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_map_jpg);
        viewBackground.eventMode = "static";
        this.addChild(viewBackground);

        this.marketplaceMap = new MarketplaceMap(
            this.marketplaceResources,
            this.canvasScaler,
            this.marketplaceController,
            this.marketplaceMapModel,
            this.marketplaceMapViewModel,
            this.playerModel,
            this.analyticsProvider
        );
        this.addChild(this.marketplaceMap);
        this.mapMask = new Sprite(Texture.WHITE);
        this.marketplaceMap.mask = this.mapMask;
        this.addChild(this.mapMask);

        this.zoomButton = new ButtonWithScaleAnimation(this.marketplaceResources.assets_views_marketplace_zoom_button_png);
        this.addChild(this.zoomButton);

        this.buildingFilterSwitcher = new BuildingFilterSwitcher(
            this.marketplaceResources.assets_views_marketplace_check_box_switch_off_png,
            this.marketplaceResources.assets_views_marketplace_check_box_swith_on_png
        );
        this.filterSwitchHandle = this.filterSwitchHandle.bind(this);
        this.addChild(this.buildingFilterSwitcher);

        this.leftBox = new VBox(20);
        this.discountContainer = new DiscountContainer(this.commonResources, this.benefitsModel);

        this.leftBox.addChild(this.discountContainer);
        this.addChild(this.leftBox);

        this.backButton = new DefaultButton(this.commonResources.assets_common_ui_x_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.addChild(this.backButton);

        this.headerText = new Text(MarketplaceView.HEADER_TEXT, new HeaderTextStyle());
        this.addChild(this.headerText);

        this.newsPresenter = new MarketplaceNewsPresenter(this.marketplaceResources, this.marketplaceNewsModel, this.canvasScaler);
        this.addChild(this.newsPresenter);

        this.scrollBox = new MarketplaceScrollBox({
            type: 'horizontal',
            width: this.canvasScaler.referenceWidth,
            height: new Sprite(this.marketplaceResources.assets_views_marketplace_glow_png).height,
            horPadding: -80,
            elementsMargin: -135
        });
        this.scrollBox.onScroll.add(this.onScroll.bind(this));
        this.addChild(this.scrollBox);
        this.createScrollBoxItems();

        this.marketplaceFullBuilding = new FullBuildingContainer(
            this.cancellationToken,
            this.marketplaceResources,
            this.commonResources,
            this.userProfileResources,
            this.canvasScaler,
            this.marketplaceBuildingModel,
            this.marketplaceMapViewModel,
            this.marketplaceCards,
            this.marketplaceController,
            this.buildingArea,
            this.analyticsProvider,
            this.logger,
            this.playerModel,
            this.web3Model
        );
        this.marketplaceFullBuilding.onLeftButtonClick.add(this.scrollToLeft.bind(this));
        this.marketplaceFullBuilding.onRightButtonClick.add(this.scrollToRight.bind(this));
        this.addChild(this.marketplaceFullBuilding);
    }

    private addedToStage(): void {
        this.zoomButton.visible = false;
        this.marketplaceMap.onMapZoomChange.add((isZoomed: boolean) => {
            this.zoomButton.visible = isZoomed;
        });
        if (this.buildingArea != -1) {
            this.scrollToRight();
            this.marketplaceFullBuilding.show();
        } else {
            this.marketplaceFullBuilding.visible = false;
        }
    }

    private async filterSwitchHandle(checked: boolean): Promise<void> {
        this.marketplaceMapViewModel.returnOnlyBuildingsByMon.setValue(checked);
        this.marketplaceMapViewModel.orderByPriceInMon.setValue(checked ? "DESC" : null);
        const currentMapFilter = this.marketplaceMapViewModel.currentMapFilter.value;
        await Promise.all([
            this.marketplaceController.updateAreas(),
            this.marketplaceController.updateMapFilter(new MapFilter(currentMapFilter.area)),
        ]);
    }

    private async replaceScrollBoxItems(): Promise<void> {
        this.scrollBoxUpdateAnimation.to(this.scrollBox, { alpha: 0, duration: 0.25, ease: "power2.in" });
        await this.scrollBoxUpdateAnimation.play();
        this.scrollBoxUpdateAnimation.clear();
        this.scrollBox.removeItems();
        this.clearMarketplaceCards();
        this.createScrollBoxItems();
        this.scrollBox.scrollTop();
        this.scrollBoxUpdateAnimation.to(this.scrollBox, { alpha: 1, duration: 0.25, ease: "power2.in" });
        this.scrollBoxUpdateAnimation.add(() => this.glowClosestCard(), "<25%");
        await this.scrollBoxUpdateAnimation.play();
        this.scrollBoxUpdateAnimation.clear();
    }

    private async addNewItemsToScrollBox(): Promise<void> {
        this.scrollBox.removeItem(0);
        this.marketplaceMapModel.buildings.value.forEach((item) => this.marketplaceCardFactory(item));
        this.scrollBox.addItems(this.marketplaceCards);
        const latestFakeScrollBoxItem = new Sprite(this.marketplaceResources.assets_views_marketplace_glow_png);
        latestFakeScrollBoxItem.anchor.set(0.5);
        latestFakeScrollBoxItem.alpha = 0;
        this.scrollBox.addItem(latestFakeScrollBoxItem);
        this.scrollBox.layout();
    }

    private createScrollBoxItems(): void {
        const firstFakeScrollBoxItem = new Sprite(this.marketplaceResources.assets_views_marketplace_glow_png);
        firstFakeScrollBoxItem.anchor.set(0.5);
        firstFakeScrollBoxItem.alpha = 0;
        this.scrollBox.addItem(firstFakeScrollBoxItem);
        this.marketplaceMapModel.buildings.value.forEach((item) => this.marketplaceCardFactory(item));
        this.scrollBox.addItems(this.marketplaceCards);
        const latestFakeScrollBoxItem = new Sprite(this.marketplaceResources.assets_views_marketplace_glow_png);
        latestFakeScrollBoxItem.anchor.set(0.5);
        latestFakeScrollBoxItem.alpha = 0;
        this.scrollBox.addItem(latestFakeScrollBoxItem);
        this.scrollBox.layout();
        this.glowClosestCard();
    }

    private marketplaceCardFactory(marketplaceListItem: MarketplaceListItem): void {
        const marketplaceCard = new MarketplaceCard(this.marketplaceResources, this.commonResources, marketplaceListItem, this.marketplaceMapViewModel);
        marketplaceCard.onCardClick.add(() => {
            this.scrollToMarketplaceItem(marketplaceCard);
            this.marketplaceFullBuilding.show();
        });
        this.marketplaceCards.push(marketplaceCard);
    }

    private async onScroll(): Promise<void> {
        const marketplaceCards = this.marketplaceCards;
        let leftCard = marketplaceCards[marketplaceCards.indexOf(this.closestCard) - 1];
        let rightCard = marketplaceCards[marketplaceCards.indexOf(this.closestCard) + 1];
        const center = this.canvasScaler.referenceWidth * 0.5;
        if (marketplaceCards.indexOf(this.closestCard) - 1 >= 0) {
            if (leftCard && Math.abs(leftCard.getGlobalPosition().x - center) < this.canvasScaler.referenceWidth / 3 && Math.abs(leftCard.getGlobalPosition().x - center) < Math.abs(this.closestCard.getGlobalPosition().x - center)) {
                this.closestCard.hideGlow();
                this.closestCard = leftCard;
                await this.closestCard.showGlow();
            }
        }
        if (marketplaceCards.indexOf(this.closestCard) + 1 < marketplaceCards.length) {
            if (rightCard && Math.abs(rightCard.getGlobalPosition().x - center) < this.canvasScaler.referenceWidth / 3 && Math.abs(rightCard.getGlobalPosition().x - center) < Math.abs(this.closestCard.getGlobalPosition().x - center)) {
                this.closestCard.hideGlow();
                this.closestCard = rightCard;
                await this.closestCard.showGlow();
            }
        }
        const mapFilter = this.marketplaceMapViewModel.currentMapFilter.value;
        if (mapFilter.limit + mapFilter.offset === marketplaceCards.length && !this.scrollBoxInLoadedState) {
            const isLoadNextMapFilter = this.scrollBox.isItemVisible(marketplaceCards[marketplaceCards.length - mapFilter.limit / 4]);
            if (isLoadNextMapFilter) {
                this.scrollBoxInLoadedState = true;
                this.marketplaceController.updateMapFilterWithNextOffset();
            }
        }
    }

    private scrollToLeft(): void {
        const marketplaceCards = this.marketplaceCards;
        const selectedItemIndex = marketplaceCards.indexOf(this.marketplaceMapViewModel.selectedMarketplaceCard.value);
        const nextIndex = selectedItemIndex - 1;
        if (nextIndex >= 0) {
            this.scrollToMarketplaceItem(marketplaceCards[nextIndex]);
        }
    }

    private scrollToRight(): void {
        const marketplaceCards = this.marketplaceCards;
        const selectedItemIndex = marketplaceCards.indexOf(this.marketplaceMapViewModel.selectedMarketplaceCard.value);
        const nextIndex = selectedItemIndex + 1;
        if (nextIndex <= marketplaceCards.length - 1) {
            this.scrollToMarketplaceItem(marketplaceCards[nextIndex]);
        }
    }

    private async onZoomClick(): Promise<void> {
        this.zoomButton.eventMode = "none";
        await this.marketplaceMap.mapZoomOut();
        this.zoomButton.eventMode = "static";
    }

    private async onExitClick(): Promise<void> {
        this.operationControls.complete();
    }

    private async glowClosestCard(): Promise<void> {
        const center = this.canvasScaler.referenceWidth * 0.5;
        for (const card of this.marketplaceCards) {
            const cardPosition = card.getGlobalPosition().x;
            if (Math.abs(cardPosition - center) < this.canvasScaler.getSize().width / 3) {
                if (this.closestCard != null) {
                    this.closestCard.hideGlow();
                }
                card.showGlow();
                this.closestCard = card;
            }
        }
    }

    private async scrollToMarketplaceItem(marketplaceCard: MarketplaceCard): Promise<void> {
        this.scrollBox.scrollTo(this.marketplaceCards.indexOf(marketplaceCard) + 1);
        this.marketplaceController.updateSelectedMarketplaceCard(marketplaceCard);
        this.cardClickAnimation.add(() => {
            this.glowClosestCard();
            this.onScroll();
        }, "0.05");
        await this.cardClickAnimation.play();
        this.cardClickAnimation.clear();
    }

    private async discountButtonClickHandle(): Promise<void> {
        this.scrollBox.setWheelScrollable(false);
        await this.marketplaceController.showMyDiscountPopup(this.cancellationToken);
        this.scrollBox.setWheelScrollable(true);
    }

    private clearMarketplaceCards(): void {
        for (const card of this.marketplaceCards) {
            card.destroy({ children: true, texture: false, baseTexture: false });
        }
        this.marketplaceCards.length = 0;
    }

    protected layout(_width: number, height: number): void {
        this.headerText.position.set(265, 105);
        this.backButton.position.set(77 - this.backButton.width * 0.5, 95);
        this.zoomButton.position.set(688, 1052);
        this.mapMask.width = this.marketplaceMap.width;
        this.mapMask.height = this.marketplaceMap.height;
        this.mapMask.position.y = MarketplaceView.HEADER_HEIGHT;
        this.scrollBox.position.set(0, height - this.scrollBox.height);
        this.newsPresenter.position.set(0, 233);
        this.buildingFilterSwitcher.position.set(30, 355);
        this.leftBox.position.set(20, this.buildingFilterSwitcher.position.y + this.buildingFilterSwitcher.height + 30);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.scrollBox.onScroll.removeAll();
        this.zoomButton.onUp.remove(this.onZoomClick, this);
        this.backButton.onUp.remove(this.onExitClick, this);
        this.marketplaceMap.onMapZoomChange.removeAll();
        this.marketplaceFullBuilding.onLeftButtonClick.removeAll();
        this.marketplaceFullBuilding.onRightButtonClick.removeAll();
        this.marketplaceFullBuilding.onTransactionHandled.remove(this.transactionCompleteHandle, this);
        this.marketplaceMapModel.propertyChanged.remove(this.onMarketplaceMapModelPropertyChanged, this);
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.discountContainer.button.onUp.remove(this.discountButtonClickHandle, this);
        this.buildingFilterSwitcher.switcher.onChange.disconnect(this.filterSwitchHandle);
        this.scrollBoxUpdateAnimation?.kill();
        this.cardClickAnimation?.kill();
        this.clearMarketplaceCards();
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(
            new TextStyle({
                align: "center",
                fill: ["#00ffa4", "#11c1e1"],
                fillGradientType: 1,
                fontFamily: GameFont.DEFAULT_FONT,
                fontSize: 74,
            })
        );
    }
}
